// 基本テーマ定義 - 橋本病患者さん向けの優しい色合い
export const basicThemes = {
  // 1. ベーシックブルー（デフォルト）
  lightBlue: {
    id: 'lightBlue',
    name: '🔵 ベーシックブルー',
    description: 'シンプルで清潔感のある基本テーマ',
    category: 'basic',
    colors: {
      primary: 'sky',
      secondary: 'slate',
      accent: 'cyan',
      neutral: 'gray',
      // 背景色 - より優しい色合い
      background: 'sky-50',        // 全体背景（とても薄い水色）
      cardBg: 'white',
      // テキスト色（読みやすさ重視）
      textPrimary: 'slate-700',    // 少し明るく
      textSecondary: 'slate-500',
      // ボーダー
      border: 'sky-200',
      borderLight: 'sky-100',
      // ボタン - 優しい色合い
      button: 'sky-500',
      buttonHover: 'sky-600',
      // ヘッダー（グロナビ）- 基本テーマは共通の白
      headerBg: 'white',
      headerText: 'slate-700',
      headerHover: 'gray-50',
      // フッター - 基本テーマは共通のダークグレー
      footerBg: 'slate-800',
      footerText: 'white',
      footerLink: 'slate-300'
    }
  },

  // 2. ベーシックグリーン
  elegantGreen: {
    id: 'elegantGreen',
    name: '🟢 ベーシックグリーン',
    description: 'ナチュラルで落ち着いた緑の基本テーマ',
    category: 'basic',
    colors: {
      primary: 'teal',
      secondary: 'slate',
      accent: 'emerald',
      neutral: 'gray',
      // 背景色 - やさしいミント色
      background: 'teal-50',       // 全体背景（とても薄いミント）
      cardBg: 'white',
      // テキスト色（読みやすさ重視）
      textPrimary: 'slate-700',
      textSecondary: 'slate-500',
      // ボーダー
      border: 'teal-200',
      borderLight: 'teal-100',
      // ボタン - 優しいミント
      button: 'teal-500',
      buttonHover: 'teal-600',
      // ヘッダー（グロナビ）- 基本テーマは共通の白
      headerBg: 'white',
      headerText: 'slate-700',
      headerHover: 'gray-50',
      // フッター - 基本テーマは共通のダークグレー
      footerBg: 'slate-800',
      footerText: 'white',
      footerLink: 'slate-300'
    }
  },

  // 3. ベーシックオレンジ
  warmOrange: {
    id: 'warmOrange',
    name: '🟠 ベーシックオレンジ',
    description: '温かみのあるオレンジの基本テーマ',
    category: 'basic',
    colors: {
      primary: 'orange',
      secondary: 'slate',
      accent: 'amber',
      neutral: 'gray',
      // 背景色 - 柔らかいピーチ色
      background: 'orange-50',     // 全体背景（とても薄いピーチ）
      cardBg: 'white',
      // テキスト色（読みやすさ重視）
      textPrimary: 'slate-700',
      textSecondary: 'slate-500',
      // ボーダー
      border: 'orange-200',
      borderLight: 'orange-100',
      // ボタン - 優しいオレンジ
      button: 'orange-500',
      buttonHover: 'orange-600',
      // ヘッダー（グロナビ）- 基本テーマは共通の白
      headerBg: 'white',
      headerText: 'slate-700',
      headerHover: 'gray-50',
      // フッター - 基本テーマは共通のダークグレー
      footerBg: 'slate-800',
      footerText: 'white',
      footerLink: 'slate-300'
    }
  },

  // 4. ベーシックパープル
  elegantPurple: {
    id: 'elegantPurple',
    name: '🟣 ベーシックパープル',
    description: '上品な紫の基本テーマ',
    category: 'basic',
    colors: {
      primary: 'violet',
      secondary: 'slate',
      accent: 'purple',
      neutral: 'gray',
      // 背景色 - 優しいラベンダー色
      background: 'violet-50',     // 全体背景（とても薄いラベンダー）
      cardBg: 'white',
      // テキスト色（読みやすさ重視）
      textPrimary: 'slate-700',
      textSecondary: 'slate-500',
      // ボーダー
      border: 'violet-200',
      borderLight: 'violet-100',
      // ボタン - 優しいラベンダー
      button: 'violet-500',
      buttonHover: 'violet-600',
      // ヘッダー（グロナビ）- 基本テーマは共通の白
      headerBg: 'white',
      headerText: 'slate-700',
      headerHover: 'gray-50',
      // フッター - 基本テーマは共通のダークグレー
      footerBg: 'slate-800',
      footerText: 'white',
      footerLink: 'slate-300'
    }
  }
};

// Tailwindクラス生成ヘルパー関数
export const generateThemeClasses = (theme) => {
  if (!theme || !theme.colors) {
    return generateThemeClasses(basicThemes.lightBlue); // デフォルト
  }

  const colors = theme.colors;
  
  return {
    // 背景
    bg: `bg-${colors.background}`,
    bgCard: `bg-${colors.cardBg}`,
    background: `bg-${colors.background}`,  // 全体背景用
    pageBackground: `bg-${colors.background}`,  // 既存システムとの互換性
    
    // テキスト
    textPrimary: `text-${colors.textPrimary}`,
    textSecondary: `text-${colors.textSecondary}`,
    
    // ボーダー
    border: `border-${colors.border}`,
    borderLight: `border-${colors.borderLight}`,
    
    // ボタン（既存との互換性）
    primaryButton: `bg-${colors.button} hover:bg-${colors.buttonHover} text-white`,
    secondaryButton: `bg-${colors.neutral}-100 hover:bg-${colors.neutral}-200 text-${colors.neutral}-800`,
    button: `bg-${colors.button} hover:bg-${colors.buttonHover} text-white`,
    buttonSecondary: `bg-${colors.neutral}-100 hover:bg-${colors.neutral}-200 text-${colors.neutral}-800`,
    
    // カード
    card: `bg-${colors.cardBg} border border-${colors.borderLight}`,
    cardHeader: `bg-${colors.primary}-50 border-b border-${colors.borderLight}`,
    
    // 特殊効果（プレミアム用に予約）
    premiumEffect: '', // 基本テーマでは空
    
    // アクセントカラー
    accent: `text-${colors.accent}-600`,
    accentBg: `bg-${colors.accent}-100`,
    
    // ヘッダー（グロナビ）
    header: `bg-${colors.headerBg} text-${colors.headerText}`,
    headerBg: `bg-${colors.headerBg}`,
    headerText: `text-${colors.headerText}`,
    navLink: `text-${colors.headerText} hover:bg-${colors.headerHover}`,
    
    // フッター
    footer: `bg-${colors.footerBg} text-${colors.footerText}`,
    footerBg: `bg-${colors.footerBg}`,
    footerText: `text-${colors.footerText}`,
    footerLink: `text-${colors.footerLink} hover:text-${colors.footerText}`,
    
    // リンク
    link: `text-${colors.primary}-600 hover:text-${colors.primary}-800`,
    
    // 入力フィールド
    input: `border-${colors.border} focus:border-${colors.primary}-500 focus:ring-${colors.primary}-500`,
    
    // 成功・エラーなどの状態色（共通）
    success: 'text-green-600 bg-green-50 border-green-200',
    warning: 'text-yellow-600 bg-yellow-50 border-yellow-200',
    error: 'text-red-600 bg-red-50 border-red-200',
    info: `text-${colors.primary}-600 bg-${colors.primary}-50 border-${colors.primary}-200`
  };
};